import { CurrencyPipe } from '@angular/common';
import { Component, Input, OnChanges, ViewChild, ElementRef, AfterViewInit, EventEmitter, Output } from '@angular/core';
import { Chart, ChartDataset, ChartOptions, ChartType, registerables } from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
Chart.register(...registerables, ChartDataLabels);
Chart.defaults.font.size = 11;

@Component({
  selector: 'ppl-auth-bar-chart',
  standalone: true,
  template: `<div class="bar-chart-container"><canvas #barCanvas></canvas></div>`,
  styleUrls: ['./auth-bar-chart.component.css'],
  providers: [CurrencyPipe]
})
export class AuthBarChartComponent implements OnChanges, AfterViewInit {
  @ViewChild('barCanvas') barCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() inputLabels: (string | string[])[] = [];
  @Input() data: ChartDataset<'bar'>[] = [];
  @Output() chartClick = new EventEmitter<any>();
  @Input() indexAxis: 'x' | 'y' = 'x';
  @Input() stackedXAxis: boolean = true;
  @Input() stackedYAxis: boolean = true;
  /**
   * 'nonzero' drops the $0.00 labels that swamp a sparse chart; the table view still carries them.
   * 'total' prints one label per stacked column (sum of the visible segments) above its top
   * segment, so small segments never collide; the per-segment amounts move to the tooltip.
   */
  @Input() labelValues: 'all' | 'nonzero' | 'total' = 'all';
  private chart!: Chart<'bar'>;

  constructor(private currencyPipe: CurrencyPipe) { }

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnChanges(): void {
    if (this.chart) {
      this.updateChart();
    }
  }

  private createChart() {
    if (!this.barCanvas) return;

    this.chart = new Chart(this.barCanvas.nativeElement, {
      type: 'bar',
      data: {
        labels: this.inputLabels,
        datasets: this.data,
      },
      options: {
        indexAxis: this.indexAxis,
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
          if (elements.length) {
            this.chartClick.emit(elements);
          }
        },
        // In 'total' mode the hover lists every category in the column, since the bars only carry the sum.
        interaction: this.labelValues === 'total' ? { mode: 'index', intersect: false } : undefined,
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            filter: (item) => this.labelValues !== 'total' || !!(item.raw as number),
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${this.formatCurrency(ctx.raw as number)}`,
              footer: (items) => this.labelValues === 'total' && items.length > 1
                ? `Total: ${this.formatCurrency(items.reduce((sum, item) => sum + ((item.raw as number) || 0), 0))}`
                : '',
            },
          },
          datalabels: this.labelValues === 'total' ? this.totalLabelOptions() : {
            display: (ctx) => this.labelValues === 'all' || !!(ctx.dataset.data[ctx.dataIndex] as number),
            color: '#000',
            font:{
              weight: 'bold',
              size: 9,
            },
            clamp: true,
            formatter: (value: number) => this.formatCurrency(value),
            align: (ctx) => ctx.datasetIndex === 0 ? 'center' : 'end',
            anchor: (ctx) => ctx.datasetIndex === 0 ? 'center' : 'end',
            offset: (ctx) => {
              const value = ctx.dataset.data[ctx.dataIndex] as number;
              return ctx.datasetIndex === 0 ? 0 : (value < 100 ? -10 : -6);
            }
          }
        },
        // Mark specs: capped bar thickness, rounded data-end square at the baseline,
        // and hairline solid grid one step off the surface.
        datasets: {
          bar: { maxBarThickness: 24, borderRadius: 4, borderSkipped: 'start' }
        },
        scales: {
          x: {
            stacked: this.stackedXAxis,
            barPercentage: 0.6,
            categoryPercentage: 0.7,
            grid: { display: false },
            border: { color: '#ececec' }
          },
          y: {
            stacked: this.stackedYAxis,
            beginAtZero: true,
            ticks: {
              callback: (value) => `$${Number(value).toLocaleString('en-US')}`,
            },
            grid: { color: '#ececec', drawTicks: false },
            border: { display: false },
            grace: '15%'
          },
        },
      } as ChartOptions<'bar'>,
    });
  }

  private formatCurrency(value: number): string {
    return this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2') ?? '';
  }

  /** Column total, drawn once on the topmost visible non-zero segment of each stack. */
  private totalLabelOptions() {
    const visibleValues = (ctx: Context) => ctx.chart.data.datasets
      .map((dataset, index) => ctx.chart.isDatasetVisible(index) ? (dataset.data[ctx.dataIndex] as number) || 0 : 0);
    return {
      display: (ctx: Context) => {
        const values = visibleValues(ctx);
        return !!values[ctx.datasetIndex] && values.every((value, index) => index <= ctx.datasetIndex || !value);
      },
      formatter: (_: number, ctx: Context) => this.formatCurrency(visibleValues(ctx).reduce((sum, value) => sum + value, 0)),
      color: '#333',
      font: { weight: 'bold' as const, size: 11 },
      anchor: 'end' as const,
      align: 'end' as const,
      offset: 4,
      clamp: true,
    };
  }

  private updateChart() {
    this.chart.data.labels = this.inputLabels;
    this.chart.data.datasets = this.data;
    this.chart.update();
  }
}
